/**
 * Agent Run show — one executor invocation on the shared Inspector grammar
 * (design-system §5 / frontend-entity-coverage §2.3).
 *
 * The matrix requires: executor, skill, status, context packet, prompt URL,
 * output URL, error, an **output-validation** read, plus a **retry** action
 * (POST /api/agent-runs/:id/retry, `11 §3.3`) and an **open trace** action
 * (the full AgentTrace at /agent-runs/:id/trace).
 *
 * Layout: Overview (executor/skill/status/timing) · Body (stored artifact URLs,
 * output summary, output-validation, error) · Relations (Skill / Artifact /
 * RuleRun + produced Claims/Evidence/Events teaser) · Audit (generic timeline).
 * The output-validation panel (`requires_human_review`, produced counts) is the
 * boxed-agent Approve/Reject surface and is `[PROPOSED]` — labeled-empty teaser.
 */
import { useShow, useNavigation, useGo, type HttpError } from "@refinedev/core";
import { Bot, ClipboardCheck, FileBox, Network, Repeat, Sparkles, Zap } from "@/components/ui/icon";
import type { AgentRun } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/app";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  type RelationGroup,
  TechnicalId,
} from "@/components/inspector";
import { ShowPageSkeleton, ShowPageError } from "@/components/inspector/ShowPage";
import { formatDateTime } from "../_shared/runs-ui";
import { useApiAction } from "../_shared/useApiAction";
import { OutputValidation } from "./OutputValidation";

export function AgentRunShow() {
  const { query } = useShow<AgentRun, HttpError>();
  const { list } = useNavigation();
  const go = useGo();
  const { run, loading } = useApiAction();

  const record = query.data?.data;

  if (query.isLoading) return <ShowPageSkeleton />;
  if (query.isError || !record) {
    return (
      <ShowPageError
        message={query.error?.message ?? "Agent run not found."}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const retry = () => {
    void run(`/agent-runs/${record.id}/retry`, {
      invalidateResources: ["agent-runs", "events"],
      successMessage: "Agent run re-dispatched.",
    });
  };

  const relations: RelationGroup[] = [
    {
      title: "References",
      items: [
        { label: "Skill", icon: Sparkles, resource: "skills", id: record.skill_id },
        {
          label: "Artifact",
          icon: FileBox,
          resource: "artifacts",
          id: record.artifact_id,
        },
        {
          label: "Rule run",
          icon: Zap,
          resource: "rule-runs",
          id: record.rule_run_id,
        },
      ],
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title="Agent Run"
        subtitle="An executor invocation dispatched by a rule — its inputs, output, and trace."
        status={record.status}
        primaryAction={
          <Button
            size="sm"
            onClick={retry}
            disabled={loading}
            data-testid="agent-run-retry"
          >
            <Repeat className="mr-2 h-4 w-4" />
            {loading ? "Retrying…" : "Retry"}
          </Button>
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => go({ to: `/agent-runs/${record.id}/trace` })}
              data-testid="agent-run-open-trace"
            >
              <Bot className="mr-2 h-4 w-4" /> Open trace
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => go({ to: `/agent-runs/${record.id}/trace-viewer` })}
              data-testid="agent-run-open-trace-viewer"
            >
              <Network className="mr-2 h-4 w-4" /> Trace Viewer
            </Button>
            <Button variant="ghost" size="sm" onClick={() => list("agent-runs")}>
              Back
            </Button>
          </>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Executor">{record.executor_type}</Field>
          <Field label="Skill">
            <RefLink resource="skills" id={record.skill_id} />
          </Field>
          <Field label="Created">{formatDateTime(record.created_at)}</Field>
          <Field label="Started">{formatDateTime(record.started_at)}</Field>
          <Field label="Finished">{formatDateTime(record.finished_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Body">
        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Output summary
            </h4>
            {record.output_summary ? (
              <Markdown source={record.output_summary} />
            ) : (
              <p className="text-sm text-muted-foreground">
                No output summary yet.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <h4 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <ClipboardCheck className="h-3.5 w-3.5" /> Output validation
            </h4>
            <OutputValidation run={record} />
          </div>

          {record.error ? (
            <div className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wide text-destructive">
                Error
              </h4>
              <pre className="whitespace-pre-wrap rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
                {record.error}
              </pre>
            </div>
          ) : null}

          <div className="flex justify-start">
            <Button
              variant="outline"
              size="sm"
              onClick={() => go({ to: `/agent-runs/${record.id}/trace` })}
            >
              <Bot className="mr-2 h-4 w-4" /> View full trace
            </Button>
          </div>
        </div>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock groups={relations} />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={String(record.id)} />
          </Field>
          <Field label="Rule run">
            <RefLink resource="rule-runs" id={record.rule_run_id} />
          </Field>
          <Field label="Artifact">
            <RefLink resource="artifacts" id={record.artifact_id} />
          </Field>
          <Field label="External run id">
            {record.external_run_id ? (
              <span className="font-mono text-xs">{record.external_run_id}</span>
            ) : null}
          </Field>
          <Field label="Context snapshot URL" full>
            {record.context_snapshot_url ? (
              <span className="font-mono text-xs">
                {record.context_snapshot_url}
              </span>
            ) : null}
          </Field>
          <Field label="Prompt URL" full>
            {record.prompt_url ? (
              <span className="font-mono text-xs">{record.prompt_url}</span>
            ) : null}
          </Field>
          <Field label="Output URL" full>
            {record.output_url ? (
              <span className="font-mono text-xs">{record.output_url}</span>
            ) : null}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock
          entityType="agent-runs"
          entityId={record.id}
          provenance={[
            { label: "Rule run", resource: "rule-runs", id: record.rule_run_id },
            { label: "Skill", resource: "skills", id: record.skill_id },
          ]}
        />
      </InspectorSection>
    </Inspector>
  );
}

export default AgentRunShow;
